// Does business logic on the housings trends data and transforms it to a format
// easily used by the frontend components
import {
  AloftDate,
  MarketTrendsType,
  assert,
  isUndefined,
  syntaxFNMA,
} from '@aloft/toolkit-shared';
import { IsoMonth } from '@aloft/toolkit-shared/lib/syntax/fnma';

import { makeCompMonthMap } from '../utils/makeCompMonthMap.ts';
import { CompTransactions, MonthGrouping, ZillowHomeValueIndexTrendsData } from './types.ts';

const monthlyTrendsMonthToAloftDate = (year: number, month: number) => {
  const formattedMonth = String(month).padStart(2, '0'); // firefox errors if the date is formatted like `2024-1`
  const native = new Date(`${year}-${formattedMonth}`);
  return AloftDate.fromIsoString(native.toISOString().split('T')[0]);
};

const isMissingDataInPreviousTwoYears = (
  effectiveDate: AloftDate,
  historical: MonthGrouping[],
  projection: MonthGrouping[],
) => {
  const twentyFourMonthsPrior = effectiveDate.subtractMonths(24);
  const twentyFourMonthsPriorIsoMonth = syntaxFNMA.aloftDateToIsoMonth(twentyFourMonthsPrior);
  const startIndex = historical?.findIndex((val) => val.month === twentyFourMonthsPriorIsoMonth);

  const historicalPastTwoYears = historical.slice(startIndex + 1);
  return historicalPastTwoYears.length + projection.length < 24;
};

/** @deprecated */
export const getHousingTrendsData = (
  isZillow: boolean,
  effectiveDate: AloftDate | undefined,
  data: MarketTrendsType | undefined,
  compTransactions: CompTransactions,
  compSequence: string[],
): ZillowHomeValueIndexTrendsData | undefined => {
  if (isUndefined(data) || isUndefined(effectiveDate)) return undefined;

  const effectiveDateMonth = effectiveDate.getMonth();
  const effectiveDateYear = effectiveDate.getYear();

  // Sort the historical data just in case
  const sortedHistoricalData = [...data.monthly_trends.historical_data].sort(
    (a, b) => a.year - b.year || a.month - b.month,
  );

  if (sortedHistoricalData.length === 0 || isUndefined(data.monthly_trends.one_month_projection)) {
    console.warn('No projection -> not enough data for analysis');
    return undefined;
  }

  const lastHistoricalMonth = sortedHistoricalData[sortedHistoricalData.length - 1];
  assert(lastHistoricalMonth);
  const projectedMonthAloftDate = monthlyTrendsMonthToAloftDate(
    lastHistoricalMonth.year,
    lastHistoricalMonth.month,
  )?.addMonths(1);

  const effectiveDateIndex = sortedHistoricalData.findIndex(
    (val) => val.year === effectiveDateYear && val.month === effectiveDateMonth,
  );

  const effectiveDateIsProjectedMonth =
    projectedMonthAloftDate?.getMonth() === effectiveDateMonth &&
    projectedMonthAloftDate?.getYear() === effectiveDateYear;

  const effectiveDateIsProjectedMonthPlusOne =
    projectedMonthAloftDate?.addMonths(1).getMonth() === effectiveDateMonth &&
    projectedMonthAloftDate?.addMonths(1).getYear() === effectiveDateYear;
  let endIndex = effectiveDateIndex;
  if (effectiveDateIndex < 0) {
    if (!effectiveDateIsProjectedMonth && !effectiveDateIsProjectedMonthPlusOne) {
      // Effective date isn't within the historical data and is not the projected month
      // or the projected month + 1 -> not enough data for analysis
      console.warn('Effective date not in the data -> not enough data for analysis');
      return undefined;
    }
    endIndex = sortedHistoricalData.length - 1;
  }
  const historicalDataRange = sortedHistoricalData.slice(0, endIndex + 1);

  if (historicalDataRange.length < 24) {
    console.warn('Less than 24 months of data -> not enough data for analysis');
    return undefined;
  }

  // At this point, the effective date is guaranteed to be either the projected month
  // or the effective date is the last element in the historicalDataRange
  const typicalValueAtEffectiveDate =
    effectiveDateIsProjectedMonth || effectiveDateIsProjectedMonthPlusOne
      ? data.monthly_trends.one_month_projection
      : historicalDataRange[historicalDataRange.length - 1]?.typicalHomeValue;

  const compMonthMap = makeCompMonthMap(compTransactions, compSequence);

  const historical: MonthGrouping[] = historicalDataRange.map((val) => {
    const month = `${val.year}-${val.month < 10 ? `0${val.month}` : val.month}` as IsoMonth;
    return {
      month,
      typicalValue: val.typicalHomeValue,
      percentChange: typicalValueAtEffectiveDate
        ? typicalValueAtEffectiveDate.sub(val.typicalHomeValue).ratio(val.typicalHomeValue) * 100
        : 0,
      numCloses: val.numCloses,
      affectedComps: compMonthMap[month]?.map((comp) => `C${comp}`).join(', '),
      medianListPrice: val.medianListPrice,
      medianClosePriceOverListPrice: val.medianClosePriceOverListPrice,
      medianDaysOnMarket: val.medianDaysOnMarket,
    };
  });

  const projectedIsoMonth =
    projectedMonthAloftDate && syntaxFNMA.aloftDateToIsoMonth(projectedMonthAloftDate);
  const projection: MonthGrouping[] | undefined =
    projectedIsoMonth &&
    effectiveDate.ge(projectedMonthAloftDate) &&
    data.monthly_trends.one_month_projection
      ? [
          {
            month: projectedIsoMonth,
            typicalValue: data.monthly_trends.one_month_projection,
            percentChange: 0,
            affectedComps: compMonthMap[projectedIsoMonth]?.map((comp) => `C${comp}`).join(', '),
          },
        ]
      : undefined;

  const effectiveDateIsoMonth = syntaxFNMA.aloftDateToIsoMonth(effectiveDate);
  const projectedMonthPlusOneMonthIsoMonth =
    projectedMonthAloftDate && syntaxFNMA.aloftDateToIsoMonth(projectedMonthAloftDate.addMonths(1));
  const shouldExtendProjection = projectedMonthPlusOneMonthIsoMonth === effectiveDateIsoMonth;
  if (projection && shouldExtendProjection && data.monthly_trends.one_month_projection) {
    // we're past the projection and need to extend it...
    projection.push({
      month: effectiveDateIsoMonth,
      typicalValue: data.monthly_trends.one_month_projection,
      percentChange: 0,
      affectedComps: compMonthMap[effectiveDateIsoMonth]?.map((comp) => `C${comp}`).join(', '),
    });
  }

  if (isMissingDataInPreviousTwoYears(effectiveDate, historical, projection ?? [])) {
    // Check if there is any missing data in the two years prior to the effective date
    console.warn('Missing data in previous two years -> not enough data for analysis');
    return undefined;
  }

  return {
    isZillow,
    historical,
    projection,
    effectiveDate,
  };
};
